import { useCallback, useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { motion } from "framer-motion";
import { Gamepad2, Loader2, Check, Crown, Zap, Rocket, AlertTriangle, LogOut, Play, Link2, X, Dices, Wand2, Download } from "lucide-react";
import { toast } from "sonner";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { api, API, formatApiError } from "@/lib/api";
import { useContent, parseList } from "@/lib/content";
import { PixelSprite } from "@/components/PixelSprites";

const TIER_CFG = [
  { id: "starter", icon: Zap },
  { id: "pro", icon: Rocket },
  { id: "ultimate", icon: Crown },
];

const GENRES = [
  { id: "space-shooter", label: "Space Shooter", seed: "A fast neon space shooter: pilot a starfighter, dodge asteroids, blast alien waves, boss every 5 waves" },
  { id: "platformer", label: "Platformer", seed: "A side-scrolling platformer: a brave robot jumps across neon rooftops collecting coins and dodging drones" },
  { id: "snake", label: "Snake+", seed: "A neon snake game with speed boosts, maze obstacles, and combo scoring for chained pickups" },
  { id: "breakout", label: "Breakout", seed: "A glowing breakout game with power-ups: multi-ball, laser paddle, and exploding bricks" },
  { id: "puzzle", label: "Puzzle", seed: "A falling-blocks puzzle game with neon pieces, chain combos, and rising speed levels" },
  { id: "racing", label: "Racing", seed: "A top-down arcade racer: drift around a glowing track against the clock, oil slicks and turbo pads" },
];

const MODELS = [
  { id: "claude-sonnet-4-6", label: "Claude Sonnet 4.6 — game-design brain" },
  { id: "gpt-5.4", label: "GPT-5.4 — fast & creative" },
];

export default function GameLab() {
  const [params] = useSearchParams();
  const [token, setToken] = useState(localStorage.getItem("tgr_member_token") || "");
  const [authMode, setAuthMode] = useState("register");
  const [auth, setAuth] = useState({ name: "", email: "", password: "" });
  const [authBusy, setAuthBusy] = useState(false);
  const [quota, setQuota] = useState(null);
  const [games, setGames] = useState([]);
  const [genre, setGenre] = useState("space-shooter");
  const [prompt, setPrompt] = useState("");
  const [model, setModel] = useState("claude-sonnet-4-6");
  const [generating, setGenerating] = useState(false);
  const [billing, setBilling] = useState("monthly");
  const [subscribing, setSubscribing] = useState("");
  const [playing, setPlaying] = useState(null);
  const [editing, setEditing] = useState(null);
  const [editPrompt, setEditPrompt] = useState("");
  const [editBusy, setEditBusy] = useState(false);

  const headers = { Authorization: `Bearer ${token}` };
  const content = useContent();
  const tiers = parseList(content["glab.tiers"]).filter((r) => r[0]).map((r, i) => ({
    ...(TIER_CFG[i] || { id: `tier${i}`, icon: Zap }),
    name: r[0] || "", monthly: parseFloat(r[1]) || 0, yearly: parseFloat(r[2]) || 0,
    tagline: r[3] || "", features: (r[4] || "").split(";").filter(Boolean), popular: r[5] === "yes",
  }));
  const gh1 = (content["glab.h1"] || "").split("|");
  const gth = (content["glab.tiers_h"] || "").split("|");

  const loadQuota = useCallback(async () => {
    try {
      const { data } = await api.get("/studio/quota", { headers });
      setQuota(data);
    } catch (e) {
      if (e.response?.status === 401) { localStorage.removeItem("tgr_member_token"); setToken(""); }
    }
  }, [token]);

  const loadGames = useCallback(async () => {
    try {
      const { data } = await api.get("/studio/games", { headers });
      setGames(data.games);
    } catch { /* ignore */ }
  }, [token]);

  useEffect(() => { if (token) { loadQuota(); loadGames(); } }, [token, loadQuota, loadGames]);

  useEffect(() => {
    if (params.get("subscribed")) {
      toast.success("Membership active — your Game Lab is unlocked!");
      loadQuota();
    }
    if (params.get("exports")) {
      toast.success("Export pack added to your account!");
      loadQuota();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (!games.some((g) => g.status === "processing")) return;
    const t = setTimeout(() => { loadGames(); loadQuota(); }, 5000);
    return () => clearTimeout(t);
  }, [games, loadGames, loadQuota]);

  useEffect(() => {
    if (!quota) return;
    if (!quota.models.includes(model)) setModel(quota.models[0]);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [quota]);

  const doAuth = async () => {
    setAuthBusy(true);
    try {
      const path = authMode === "register" ? "/auth/register" : "/auth/login";
      const payload = authMode === "register" ? auth : { email: auth.email, password: auth.password };
      const { data } = await api.post(path, payload);
      localStorage.setItem("tgr_member_token", data.access_token);
      setToken(data.access_token);
      toast.success(authMode === "register" ? "Welcome to the Game Lab — 1 free game + 3 free tweaks on us!" : "Welcome back, player!");
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setAuthBusy(false);
    }
  };

  const generate = async () => {
    if (prompt.trim().length < 10) return toast.error("Describe your game in a sentence or two.");
    setGenerating(true);
    try {
      await api.post("/studio/generate", { prompt, genre, model }, { headers });
      toast.success("Game is being built — usually 30–90 seconds. It'll appear in your library.");
      setPrompt("");
      loadGames();
      loadQuota();
    } catch (e) {
      if (e.response?.status === 402) {
        toast.error(formatApiError(e));
        document.getElementById("membership-tiers")?.scrollIntoView({ behavior: "smooth" });
      } else {
        toast.error(formatApiError(e));
      }
    } finally {
      setGenerating(false);
    }
  };

  const subscribe = async (tierId) => {
    if (!token) {
      toast.error("Create your free account first — then pick a plan.");
      document.getElementById("studio-auth")?.scrollIntoView({ behavior: "smooth" });
      return;
    }
    setSubscribing(tierId);
    try {
      const { data } = await api.post("/studio/subscribe",
        { lookup_key: `${tierId}_${billing}`, origin_url: window.location.origin }, { headers });
      window.location.href = data.checkout_url;
    } catch (e) {
      toast.error(formatApiError(e));
      setSubscribing("");
    }
  };

  const copyLink = (g) => {
    navigator.clipboard.writeText(`${window.location.origin}${API}/studio/play/${g.id}`);
    toast.success("Play link copied — share it anywhere.");
  };

  const submitEdit = async () => {
    if (editPrompt.trim().length < 4) return toast.error("Describe the tweak in a few words.");
    setEditBusy(true);
    try {
      await api.post(`/studio/games/${editing.id}/edit`, { edit_prompt: editPrompt }, { headers });
      toast.success("Tweaking your game — about a minute. The library updates itself.");
      setEditing(null);
      setEditPrompt("");
      loadGames();
    } catch (e) {
      if (e.response?.status === 402) {
        toast.error(formatApiError(e));
        document.getElementById("membership-tiers")?.scrollIntoView({ behavior: "smooth" });
      } else {
        toast.error(formatApiError(e));
      }
    } finally {
      setEditBusy(false);
    }
  };

  const exportGame = async (g) => {
    try {
      const res = await api.get(`/studio/export/${g.id}`, { headers, responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = `${(g.title || "game").toLowerCase().replace(/[^a-z0-9]+/g, "-")}.html`;
      a.click();
      URL.revokeObjectURL(url);
      toast.success("Game exported — the HTML file is yours to keep, host, and share anywhere.");
      loadQuota();
    } catch (e) {
      if (e.response?.status === 402) {
        toast.error("Export limit reached — grab an export pack below.");
        document.getElementById("export-packs")?.scrollIntoView({ behavior: "smooth" });
      } else {
        toast.error("Export failed — try again.");
      }
    }
  };

  const buyPack = async (lookupKey) => {
    if (!token) {
      toast.error("Sign in first — then grab export packs.");
      return;
    }
    try {
      const { data } = await api.post("/studio/buy-exports",
        { lookup_key: lookupKey, origin_url: window.location.origin }, { headers });
      window.location.href = data.checkout_url;
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  const exhausted = quota && quota.limit !== null && quota.used >= quota.limit;

  return (
    <div data-testid="game-lab-page">
      {/* HERO */}
      <section className="relative overflow-hidden border-b border-white/10">
        <div className="absolute inset-0 grid-bg" />
        <PixelSprite name="ghost" size={70} className="anim-drift absolute right-[12%] top-[20%] opacity-80" />
        <PixelSprite name="coin" size={48} className="anim-bob absolute left-[8%] top-[60%] opacity-80" />
        <div className="relative mx-auto max-w-7xl px-4 py-20 sm:px-6">
          <p className="mb-3 font-mono text-xs uppercase tracking-[0.35em] text-neon-cyan">{content["glab.eyebrow"]}</p>
          <h1 className="max-w-3xl font-display text-4xl font-black uppercase tracking-tight text-white sm:text-6xl" data-testid="game-lab-heading">
            {gh1[0]} <span className="text-neon-cyan glow-cyan">{gh1[1]}</span>
            <span className="text-neon-magenta glow-magenta">{gh1[2]}</span>
          </h1>
          <p className="mt-4 max-w-2xl text-base text-slate-300 sm:text-lg">
            {content["glab.intro"]}
          </p>
          <div className="mt-6 flex flex-wrap gap-3 font-mono text-xs">
            <span className="rounded-full border border-neon-cyan/40 bg-neon-cyan/5 px-4 py-2 text-neon-cyan">{content["glab.chip1"]}</span>
            <span className="rounded-full border border-white/15 px-4 py-2 text-slate-300">{content["glab.chip2"]}</span>
          </div>
        </div>
      </section>

      {/* AUTH */}
      {!token && (
        <section id="studio-auth" className="mx-auto max-w-md px-4 py-16 sm:px-6" data-testid="studio-auth-section">
          <div className="glass rounded-xl p-8">
            <Gamepad2 className="mb-4 h-8 w-8 text-neon-cyan" />
            <h2 className="font-display text-2xl font-black uppercase text-white">
              {authMode === "register" ? "Start Your Free Trial" : "Welcome Back"}
            </h2>
            <p className="mb-6 mt-1 text-sm text-slate-400">
              {authMode === "register"
                ? "Free account = 1 AI-built game + 3 free tweaks on us. No card required."
                : "Sign in to your Game Lab account."}
            </p>
            <div className="space-y-3">
              {authMode === "register" && (
                <div>
                  <Label className="text-xs text-slate-400">Name</Label>
                  <Input value={auth.name} onChange={(e) => setAuth({ ...auth, name: e.target.value })}
                    data-testid="studio-name-input" placeholder="Player One" className="border-white/10 bg-ink text-white" />
                </div>
              )}
              <div>
                <Label className="text-xs text-slate-400">Email</Label>
                <Input type="email" value={auth.email} onChange={(e) => setAuth({ ...auth, email: e.target.value })}
                  data-testid="studio-email-input" placeholder="you@example.com" className="border-white/10 bg-ink text-white" />
              </div>
              <div>
                <Label className="text-xs text-slate-400">Password</Label>
                <Input type="password" value={auth.password} onChange={(e) => setAuth({ ...auth, password: e.target.value })}
                  onKeyDown={(e) => e.key === "Enter" && doAuth()}
                  data-testid="studio-password-input" placeholder="6+ characters" className="border-white/10 bg-ink text-white" />
              </div>
              <button onClick={doAuth} disabled={authBusy} data-testid="studio-auth-submit-button"
                className="w-full rounded-md bg-neon-cyan px-4 py-3 font-display text-xs font-extrabold uppercase tracking-wide text-ink transition-colors hover:bg-white disabled:opacity-60">
                {authBusy ? "One sec..." : authMode === "register" ? "Create Free Account" : "Sign In"}
              </button>
              <button onClick={() => setAuthMode(authMode === "register" ? "login" : "register")} data-testid="studio-auth-toggle"
                className="w-full text-center font-mono text-xs text-slate-500 transition-colors hover:text-neon-cyan">
                {authMode === "register" ? "Already have an account? Sign in" : "New here? Create a free account"}
              </button>
            </div>
          </div>
        </section>
      )}

      {token && (
        <section className="mx-auto max-w-7xl px-4 py-16 sm:px-6" data-testid="studio-generator-section">
          <div className="grid gap-8 lg:grid-cols-5">
            <div className="lg:col-span-3">
              <div className="mb-6 flex flex-wrap items-center justify-between gap-3">
                <h2 className="font-display text-2xl font-black uppercase tracking-tight text-white sm:text-3xl">Build a Game</h2>
                {quota && (
                  <div className="flex items-center gap-3" data-testid="studio-quota-meter">
                    <div className="h-2 w-32 overflow-hidden rounded-full bg-white/10">
                      <div className="h-full bg-neon-cyan transition-all"
                        style={{ width: quota.limit ? `${Math.min(100, (quota.used / quota.limit) * 100)}%` : "8%" }} />
                    </div>
                    <span className="font-mono text-xs text-slate-300" data-testid="studio-quota-label">
                      {quota.plan_name}: {quota.used}{quota.limit ? `/${quota.limit}` : ""} games{quota.plan === "trial" ? " used" : " this month"}
                    </span>
                    <span className="font-mono text-[10px] text-slate-500" data-testid="studio-export-quota">
                      Exports: {quota.export_used}/{quota.export_limit}{quota.export_credits > 0 ? ` (+${quota.export_credits} pack)` : ""}
                    </span>
                    <button onClick={() => { localStorage.removeItem("tgr_member_token"); setToken(""); setQuota(null); setGames([]); }}
                      data-testid="studio-logout-button"
                      className="inline-flex items-center gap-1 font-mono text-xs text-slate-500 hover:text-neon-magenta">
                      <LogOut className="h-3 w-3" /> Sign out
                    </button>
                  </div>
                )}
              </div>

              {exhausted && (
                <div className="mb-5 flex items-center gap-3 rounded-lg border border-neon-gold/40 bg-neon-gold/10 p-4" data-testid="quota-exhausted-banner">
                  <AlertTriangle className="h-5 w-5 shrink-0 text-neon-gold" />
                  <p className="text-sm text-slate-200">
                    {quota.plan === "trial" ? "You've used your free game." : "You've hit this month's game limit."}{" "}
                    <a href="#membership-tiers" className="font-bold text-neon-gold underline">Upgrade to keep creating →</a>
                  </p>
                </div>
              )}

              <div className="glass rounded-xl p-6 sm:p-8">
                <p className="mb-2 font-mono text-[11px] uppercase tracking-[0.25em] text-slate-500">Pick a genre</p>
                <div className="mb-4 grid grid-cols-2 gap-2 sm:grid-cols-3" data-testid="genre-chips">
                  {GENRES.map((g) => (
                    <button key={g.id}
                      onClick={() => { setGenre(g.id); if (!prompt.trim()) setPrompt(g.seed); }}
                      data-testid={`genre-${g.id}`}
                      className={`rounded-md border px-3 py-2.5 font-display text-xs font-extrabold uppercase tracking-wide transition-colors ${genre === g.id ? "border-neon-cyan bg-neon-cyan/10 text-neon-cyan" : "border-white/10 text-slate-400 hover:border-neon-cyan/40"}`}>
                      {g.label}
                    </button>
                  ))}
                </div>

                <Textarea value={prompt} onChange={(e) => setPrompt(e.target.value)} rows={4}
                  data-testid="gamelab-prompt-input"
                  placeholder="Describe your game — theme, hero, enemies, power-ups, win condition. E.g. 'A snake game but I'm a delivery driver collecting pizza slices while avoiding traffic cones'"
                  className="mb-4 border-white/10 bg-ink text-white" />

                <div className="grid gap-3 sm:grid-cols-2">
                  <div>
                    <Label className="text-xs text-slate-400">AI engine — your pick</Label>
                    <select value={model} onChange={(e) => setModel(e.target.value)} data-testid="gamelab-model-select"
                      className="mt-1 w-full rounded-md border border-white/10 bg-ink px-3 py-2.5 font-mono text-xs text-white">
                      {MODELS.map((m) => (
                        <option key={m.id} value={m.id} disabled={quota && !quota.models.includes(m.id)}>
                          {m.label}{quota && !quota.models.includes(m.id) ? " (upgrade)" : ""}
                        </option>
                      ))}
                    </select>
                  </div>
                  <div className="flex items-end">
                    <button onClick={generate} disabled={generating || exhausted} data-testid="gamelab-generate-button"
                      className="inline-flex w-full items-center justify-center gap-2 rounded-md bg-neon-magenta px-6 py-3 font-display text-sm font-extrabold uppercase tracking-wide text-white transition-colors hover:bg-neon-pink disabled:opacity-50">
                      {generating ? <Loader2 className="h-4 w-4 animate-spin" /> : <Dices className="h-4 w-4" />}
                      {generating ? "Building..." : "Build My Game"}
                    </button>
                  </div>
                </div>
                <p className="mt-3 font-mono text-[10px] text-slate-500">Games are single-file HTML5 — keyboard + touch controls, score, sound, restart. Ready in about a minute.</p>
              </div>
            </div>

            {/* MY GAMES */}
            <div className="lg:col-span-2">
              <h3 className="mb-4 font-display text-lg font-extrabold uppercase text-white">My Games</h3>
              {games.length === 0 ? (
                <div className="glass flex flex-col items-center gap-3 rounded-xl p-10 text-center" data-testid="games-empty">
                  <Gamepad2 className="h-10 w-10 text-slate-600" />
                  <p className="text-sm text-slate-400">No games yet — build your first one and it'll land here.</p>
                </div>
              ) : (
                <div className="space-y-4" data-testid="game-library">
                  {games.map((g) => (
                    <div key={g.id} className="glass rounded-xl p-4" data-testid={`game-card-${g.id.slice(0, 8)}`}>
                      <div className="flex items-start justify-between gap-3">
                        <div>
                          <p className="font-display text-sm font-extrabold uppercase text-white" data-testid={`game-title-${g.id.slice(0, 8)}`}>
                            {g.title || (g.status === "processing" ? "Building..." : "Untitled Game")}
                          </p>
                          <p className="mt-0.5 line-clamp-2 text-xs text-slate-400">{g.prompt}</p>
                          <p className="mt-1 font-mono text-[10px] uppercase tracking-wider text-slate-500">{g.genre} · {g.model}</p>
                        </div>
                        {g.status === "processing" && <Loader2 className="h-5 w-5 shrink-0 animate-spin text-neon-cyan" />}
                        {g.status === "failed" && <AlertTriangle className="h-5 w-5 shrink-0 text-neon-magenta" />}
                      </div>
                      {g.status === "failed" && (
                        <p className="mt-2 font-mono text-[11px] text-neon-magenta">Build failed — your credit was refunded. Try rewording the prompt.</p>
                      )}
                      {g.status === "completed" && g.error && (
                        <p className="mt-2 font-mono text-[11px] text-neon-gold">Last tweak didn't apply — try rewording it. No tweak was used.</p>
                      )}
                      {g.status === "completed" && (
                        <div className="mt-3 flex gap-2">
                          <button onClick={() => setPlaying(g)} data-testid={`game-play-${g.id.slice(0, 8)}`}
                            className="inline-flex flex-1 items-center justify-center gap-2 rounded-md bg-neon-cyan px-4 py-2.5 font-display text-xs font-extrabold uppercase tracking-wide text-ink transition-colors hover:bg-white">
                            <Play className="h-4 w-4" /> Play
                          </button>
                          <button onClick={() => copyLink(g)} data-testid={`game-share-${g.id.slice(0, 8)}`}
                            className="inline-flex items-center justify-center gap-2 rounded-md border border-white/15 px-4 py-2.5 font-display text-xs font-extrabold uppercase text-slate-300 transition-colors hover:border-neon-magenta hover:text-neon-magenta">
                            <Link2 className="h-4 w-4" /> Share
                          </button>
                          <button onClick={() => { setEditing(g); setEditPrompt(""); }} data-testid={`game-edit-${g.id.slice(0, 8)}`}
                            className="inline-flex items-center justify-center gap-2 rounded-md border border-neon-gold/40 px-4 py-2.5 font-display text-xs font-extrabold uppercase text-neon-gold transition-colors hover:bg-neon-gold hover:text-ink">
                            <Wand2 className="h-4 w-4" /> Tweak
                          </button>
                          <button onClick={() => exportGame(g)} data-testid={`game-export-${g.id.slice(0, 8)}`}
                            className="inline-flex items-center justify-center gap-2 rounded-md border border-neon-green/40 px-4 py-2.5 font-display text-xs font-extrabold uppercase text-neon-green transition-colors hover:bg-neon-green hover:text-ink">
                            <Download className="h-4 w-4" /> Export
                          </button>
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </section>
      )}

      {/* MEMBERSHIP TIERS */}
      <section id="membership-tiers" className="border-t border-white/10 bg-inkalt/40" data-testid="membership-section">
        <div className="mx-auto max-w-7xl px-4 py-20 sm:px-6">
          <div className="mb-10 text-center">
            <p className="mb-2 font-mono text-xs uppercase tracking-[0.35em] text-neon-cyan">{content["glab.tiers_eyebrow"]}</p>
            <h2 className="font-display text-2xl font-black uppercase tracking-tight text-white sm:text-4xl">
              {gth[0]} <span className="text-neon-magenta">{gth[1]}</span>
            </h2>
            <p className="mx-auto mt-3 max-w-xl text-sm text-slate-400">
              {content["glab.tiers_text"]}
            </p>
            <div className="mt-6 inline-flex rounded-full border border-white/15 p-1" data-testid="billing-toggle">
              {["monthly", "yearly"].map((b) => (
                <button key={b} onClick={() => setBilling(b)} data-testid={`billing-${b}`}
                  className={`rounded-full px-5 py-2 font-display text-xs font-extrabold uppercase tracking-wide transition-colors ${billing === b ? "bg-neon-cyan text-ink" : "text-slate-400 hover:text-white"}`}>
                  {b === "monthly" ? "Monthly" : "Yearly · save ~20%"}
                </button>
              ))}
            </div>
          </div>

          <div className="grid gap-5 md:grid-cols-3">
            {tiers.map((t, i) => (
              <motion.div key={t.id} initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ delay: i * 0.08 }}
                className={`card-hover relative flex flex-col rounded-xl border p-7 ${t.popular ? "border-neon-magenta/60 bg-neon-magenta/5" : "border-white/10 bg-inkalt"}`}
                data-testid={`tier-${t.id}`}>
                {t.popular && (
                  <span className="absolute -top-3 left-5 rounded-full bg-neon-magenta px-3 py-1 font-mono text-[10px] font-bold uppercase tracking-widest text-white">Most Popular</span>
                )}
                <t.icon className={`mb-3 h-7 w-7 ${t.popular ? "text-neon-magenta" : "text-neon-cyan"}`} />
                <h3 className="font-display text-xl font-extrabold uppercase text-white">{t.name}</h3>
                <p className="mt-0.5 font-mono text-[11px] uppercase tracking-wider text-slate-500">{t.tagline}</p>
                <p className="mt-4">
                  <span className="font-display text-4xl font-black text-white" data-testid={`tier-price-${t.id}`}>
                    ${billing === "monthly" ? t.monthly : t.yearly}
                  </span>
                  <span className="font-mono text-xs text-slate-400"> /{billing === "monthly" ? "mo" : "yr"}</span>
                </p>
                {billing === "yearly" && (
                  <p className="mt-1 font-mono text-[11px] text-neon-green">= ${(t.yearly / 12).toFixed(0)}/mo billed yearly</p>
                )}
                <ul className="mt-5 flex-1 space-y-2">
                  {t.features.map((f) => (
                    <li key={f} className="flex items-start gap-2 text-sm text-slate-300">
                      <Check className="mt-0.5 h-4 w-4 shrink-0 text-neon-green" /> {f}
                    </li>
                  ))}
                </ul>
                <button onClick={() => subscribe(t.id)} disabled={subscribing === t.id} data-testid={`subscribe-${t.id}-button`}
                  className={`mt-7 inline-flex w-full items-center justify-center gap-2 rounded-md px-4 py-3.5 font-display text-xs font-extrabold uppercase tracking-wide transition-colors disabled:opacity-60 ${t.popular ? "bg-neon-magenta text-white hover:bg-neon-pink" : "border border-white/20 text-white hover:border-neon-cyan hover:text-neon-cyan"}`}>
                  {subscribing === t.id ? <Loader2 className="h-4 w-4 animate-spin" /> : null}
                  {quota?.plan === t.id ? "Current Plan" : `Go ${t.name}`}
                </button>
              </motion.div>
            ))}
          </div>
          <div id="export-packs" className="mx-auto mt-10 max-w-2xl rounded-xl border border-neon-gold/30 bg-neon-gold/5 p-6 text-center" data-testid="export-packs">
            <h3 className="font-display text-lg font-extrabold uppercase text-white">{content["glab.exports_h"]}</h3>
            <p className="mt-1 text-sm text-slate-400">{content["glab.exports_text"]}</p>
            <div className="mt-4 flex flex-wrap justify-center gap-3">
              <button onClick={() => buyPack("export_pack_5")} data-testid="buy-export-pack-5"
                className="rounded-md border border-neon-gold/50 px-5 py-2.5 font-display text-xs font-extrabold uppercase text-neon-gold transition-colors hover:bg-neon-gold hover:text-ink">
                5 Exports · $5
              </button>
              <button onClick={() => buyPack("export_pack_15")} data-testid="buy-export-pack-15"
                className="rounded-md bg-neon-gold px-5 py-2.5 font-display text-xs font-extrabold uppercase text-ink transition-colors hover:bg-white">
                15 Exports · $12 <span className="font-mono font-normal">(best value)</span>
              </button>
            </div>
          </div>
          <p className="mt-8 text-center font-mono text-[11px] text-slate-500">
            {content["glab.fineprint1"]}
          </p>
          <p className="mt-2 text-center font-mono text-[11px] text-slate-500">
            {content["glab.fineprint2"]}
          </p>
        </div>
      </section>

      {/* GAME PLAYER MODAL — sandboxed opaque origin */}
      {playing && (
        <div className="fixed inset-0 z-[100] flex flex-col bg-ink/95 backdrop-blur" data-testid="game-player-modal">
          <div className="flex items-center justify-between border-b border-white/10 px-4 py-3">
            <p className="font-display text-sm font-extrabold uppercase tracking-wide text-white">{playing.title || "Untitled Game"}</p>
            <div className="flex items-center gap-2">
              <button onClick={() => copyLink(playing)} data-testid="game-player-share-button"
                className="inline-flex items-center gap-2 rounded-md border border-white/15 px-3 py-1.5 font-mono text-xs text-slate-300 transition-colors hover:border-neon-cyan hover:text-neon-cyan">
                <Link2 className="h-3.5 w-3.5" /> Copy Link
              </button>
              <button onClick={() => setPlaying(null)} data-testid="game-player-close-button"
                className="inline-flex items-center gap-2 rounded-md bg-neon-magenta px-3 py-1.5 font-display text-xs font-extrabold uppercase text-white transition-colors hover:bg-neon-pink">
                <X className="h-3.5 w-3.5" /> Close
              </button>
            </div>
          </div>
          <iframe src={`${API}/studio/play/${playing.id}`} sandbox="allow-scripts" title={playing.title || "AI game"}
            className="w-full flex-1 border-0 bg-black" data-testid="game-player-frame" />
        </div>
      )}

      {/* TWEAK MODAL */}
      {editing && (
        <div className="fixed inset-0 z-[100] flex items-center justify-center bg-ink/90 p-4 backdrop-blur" data-testid="game-edit-modal">
          <div className="glass w-full max-w-lg rounded-xl p-6 sm:p-8">
            <h3 className="font-display text-xl font-extrabold uppercase text-white">
              Tweak: <span className="text-neon-gold">{editing.title || "Untitled Game"}</span>
            </h3>
            <p className="mt-1 font-mono text-xs text-slate-400" data-testid="edit-free-count">
              {Math.max(0, 3 - (editing.edits_used || 0)) > 0
                ? `${3 - (editing.edits_used || 0)} free tweaks left on this game`
                : "Free tweaks used up — this tweak counts as 1 game credit"}
            </p>
            <Textarea value={editPrompt} onChange={(e) => setEditPrompt(e.target.value)} rows={3}
              data-testid="game-edit-input"
              placeholder='Small change, big difference — e.g. "make the ship faster", "enemies should be zombies", "add a double-jump"'
              className="mt-4 border-white/10 bg-ink text-white" />
            <p className="mt-2 font-mono text-[10px] text-slate-500">Keep tweaks small and specific for best results — big rewrites are better as a new game.</p>
            <div className="mt-5 flex gap-3">
              <button onClick={() => setEditing(null)} data-testid="game-edit-cancel-button"
                className="flex-1 rounded-md border border-white/15 px-4 py-3 font-display text-xs font-extrabold uppercase text-slate-300 transition-colors hover:border-white/40">
                Cancel
              </button>
              <button onClick={submitEdit} disabled={editBusy} data-testid="game-edit-submit-button"
                className="flex-1 rounded-md bg-neon-gold px-4 py-3 font-display text-xs font-extrabold uppercase text-ink transition-colors hover:bg-white disabled:opacity-60">
                {editBusy ? "Applying..." : "Apply Tweak"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
